import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import CreateRuleModal from "../../../components/ui/popup/finances/CreateRuleModal";
import CreateCreditRuleModal from "../../../components/ui/popup/finances/CreateCreditRuleModal";
import SpendSafeguards from "./components/moneyGuardian/SpendSafeguards";
import CreditSafeguards from "./components/moneyGuardian/CreditSafeguards";
import {
  createPersonalizedRule,
  updateRule,
  createCreditRule,
  updateCreditRule,
} from "../../../api/finances/guardianAPI";
import { fetchInstitutionsAccounts } from "../../../api/finances/plaidConnectivityAPI";

/**
 * MoneyGuardian Component
 *
 * Container view managing Spend Safeguards and Credit Safeguards sections, modal triggers,
 * and account topology metadata resolution.
 *
 * @component
 * @param {Object} props Component props
 * @param {Object} props.content Localization text schema
 * @param {Function} props.onUpgradeClick Handler triggered when unsubscribed users click plan upgrades
 */
function MoneyGuardian({ content, onUpgradeClick }) {
  const planTier = useSelector(
    (state) => state.auth.user?.financial_insights?.plan_code,
  );
  const isPaidPlan = planTier !== "fi_free";

  // Spend Rule Modal UI & State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSavingRule, setIsSavingRule] = useState(false);
  const [createRuleError, setCreateRuleError] = useState("");
  const [selectedRuleToEdit, setSelectedRuleToEdit] = useState(null);

  // Credit Rule Modal UI & State
  const [isCreateCreditModalOpen, setIsCreateCreditModalOpen] = useState(false);
  const [isSavingCreditRule, setIsSavingCreditRule] = useState(false);
  const [createCreditRuleError, setCreateCreditRuleError] = useState("");
  const [selectedCreditRuleToEdit, setSelectedCreditRuleToEdit] =
    useState(null);

  // Global Refresh Loop Trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Global Financial Account Topology Metadata
  const [accountsMetadata, setAccountsMetadata] = useState([]);

  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const response = await fetchInstitutionsAccounts();
        if (response.success) {
          setAccountsMetadata(response.data);
        } else {
          console.error(
            "Failed to load connected accounting endpoints:",
            response.message,
          );
        }
      } catch (err) {
        console.error("An error occurred during metadata resolution:", err);
      }
    };

    loadMetadata();
  }, []);

  // Handler for Spend Safeguards save/update
  const handleSaveSpendRule = async (formData) => {
    setCreateRuleError("");
    setIsSavingRule(true);

    let result;
    if (selectedRuleToEdit) {
      result = await updateRule(selectedRuleToEdit.uuid, {
        type: "custom",
        ...formData,
      });
    } else {
      result = await createPersonalizedRule(formData);
    }

    if (result.success) {
      setIsCreateModalOpen(false);
      setSelectedRuleToEdit(null);
      setRefreshTrigger((prev) => prev + 1);
    } else {
      setCreateRuleError(
        result.error || "Failed to preserve configuration rules.",
      );
    }
    setIsSavingRule(false);
  };

  // Handler for Credit Safeguards save/update
  const handleSaveCreditRule = async (formData) => {
    setCreateCreditRuleError("");
    setIsSavingCreditRule(true);

    let result;
    if (selectedCreditRuleToEdit) {
      result = await updateCreditRule(selectedCreditRuleToEdit.uuid, formData);
    } else {
      result = await createCreditRule(formData);
    }

    if (result.success) {
      setIsCreateCreditModalOpen(false);
      setSelectedCreditRuleToEdit(null);
      setRefreshTrigger((prev) => prev + 1);
    } else {
      setCreateCreditRuleError(
        result.error || "Failed to preserve credit safeguard rule.",
      );
    }
    setIsSavingCreditRule(false);
  };

  return (
    <Fragment>
      <div className="money-guardian-dashboard-wrapper">
        <SpendSafeguards
          content={content}
          isPaidPlan={isPaidPlan}
          refreshTrigger={refreshTrigger}
          onUpgradeClick={onUpgradeClick}
          onAddRuleClick={() => {
            setCreateRuleError("");
            setSelectedRuleToEdit(null);
            setIsCreateModalOpen(true);
          }}
          onEditRuleClick={(rule) => {
            setCreateRuleError("");
            setSelectedRuleToEdit(rule);
            setIsCreateModalOpen(true);
          }}
        />

        <CreditSafeguards
          content={content}
          isPaidPlan={isPaidPlan}
          refreshTrigger={refreshTrigger}
          onUpgradeClick={onUpgradeClick}
          onAddRuleClick={() => {
            setCreateCreditRuleError("");
            setSelectedCreditRuleToEdit(null);
            setIsCreateCreditModalOpen(true);
          }}
          onEditRuleClick={(rule) => {
            setCreateCreditRuleError("");
            setSelectedCreditRuleToEdit(rule);
            setIsCreateCreditModalOpen(true);
          }}
        />
      </div>

      {/* Spend Safeguard Modal */}
      {isCreateModalOpen && (
        <CreateRuleModal
          content={content}
          onClose={() => {
            setIsCreateModalOpen(false);
            setSelectedRuleToEdit(null);
          }}
          onSave={handleSaveSpendRule}
          metadata={accountsMetadata}
          apiError={createRuleError}
          isSubmitting={isSavingRule}
          onClearApiError={() => setCreateRuleError("")}
          ruleToEdit={selectedRuleToEdit}
        />
      )}

      {/* Credit Safeguard Modal */}
      {isCreateCreditModalOpen && (
        <CreateCreditRuleModal
          content={content}
          onClose={() => {
            setIsCreateCreditModalOpen(false);
            setSelectedCreditRuleToEdit(null);
          }}
          onSave={handleSaveCreditRule}
          metadata={accountsMetadata}
          apiError={createCreditRuleError}
          isSubmitting={isSavingCreditRule}
          onClearApiError={() => setCreateCreditRuleError("")}
          ruleToEdit={selectedCreditRuleToEdit}
        />
      )}
    </Fragment>
  );
}

MoneyGuardian.propTypes = {
  content: PropTypes.object.isRequired,
  onUpgradeClick: PropTypes.func.isRequired,
};

export default MoneyGuardian;
